import { BillVersion, IdenticalBill } from '@enview/interface/types/bills/Bill';
import BillRelationshipType from '@enview/interface/types/bills/BillRelationshipType';
import { ReactElement } from 'react';
import ValueBadge from '../../scenes/Shared/Badges/ValueBadge';
import FeedbackIndicator from '../../scenes/Shared/Icons/FeedbackIndicator';
import './BillRelationships.scss';
import { Link } from 'react-router-dom';

type RelatedBillItemProps = {
  relationship: string;
  billId: string;
  billName: string;
  billNumber: string;
  identicalBill?: IdenticalBill;
  billVersion?: BillVersion;
  billDetailNumber?: string;
};

const PLURAL_TOOLTIP_MESSAGE =
  "Plural's intelligence engine compares bill versions and identifies relationships between those with high degrees of substantive text similarity.";

const RelatedBillItem = (props: RelatedBillItemProps): ReactElement => {
  const {
    relationship,
    billId,
    billNumber,
    billName,
    identicalBill,
    billVersion,
    billDetailNumber,
  } = props;

  let correctBillVersion = (identicalBill: IdenticalBill) => {
    return identicalBill.identicalBill.versions.find(
      (version) => version.id === identicalBill.identicalVersionId
    );
  };

  // we want to avoid saying two bill versions are 100% identical because of possible formatting differences
  const roundedScore = identicalBill?.jaccardScore
    ? Math.floor(identicalBill.jaccardScore * 100)
    : 0;
  const displayedRoundedScore = roundedScore >= 100 ? roundedScore - 1 : roundedScore;

  const billVersionUrl = identicalBill?.identicalVersionId
    ? String(identicalBill.identicalVersionId)
    : '';

  const getRelationshipMessageText = (): string => {
    if (relationship === BillRelationshipType.Companion) {
      return ' and are likely companion bills';
    } else if (relationship === BillRelationshipType.Reintroduced) {
      return ', and one is likely a re-introduced version of another';
    } else if (relationship === BillRelationshipType.Omnibus) {
      return ' and likely share an omnibus bill relationship';
    } else {
      return '';
    }
  };

  const getTooltipMessage = (): string => {
    if (!identicalBill?.jaccardScore) return '';

    const relationshipText = getRelationshipMessageText();
    const message = `These bills share ${displayedRoundedScore}% of the same sentences${relationshipText}. For more information, see`;

    return PLURAL_TOOLTIP_MESSAGE + ' ' + message;
  };

  // TODO: Add relationship type enum to related bills, use here
  const RelationshipHeader: {
    [key: string]: { relationship: string; preposition: string };
  } = {
    companion: { relationship: 'Companion Bill', preposition: 'for' },
    includedInOmnibus: { relationship: 'Omnibus Bill', preposition: 'for' },
    includedInOmnibusVerified: { relationship: 'Omnibus Bill', preposition: 'for' },
    highlySimilar: { relationship: 'Highly Similar Bill', preposition: 'to' },
    reintroduced: { relationship: 'Reintroduced Bill', preposition: 'of' },
    default: { relationship: 'Related Bill', preposition: 'to' },
  };

  const relationshipToHeader = (relationship: string): ReactElement => {
    const relationshipText =
      RelationshipHeader[relationship] || RelationshipHeader.default;
    return (
      <>
        {relationshipText.relationship}
        <span style={{ fontWeight: 'normal' }}>
          &nbsp;{relationshipText.preposition}
          &nbsp;
        </span>
      </>
    );
  };

  const relatedBillTitleText = (
    <span className="related-bill-title-text">
      {billDetailNumber}{' '}
      {billVersion?.versionName ? `(${billVersion.versionName})` : ''}
    </span>
  );
  return (
    <li key={`${billNumber}-${relationship}`} className="mt-2">
      <div className="card list-item mt-4">
        <div className="card-body">
          <div className="title mb-3">
            {identicalBill?.jaccardScore && billVersion && identicalBill?.billId ? (
              <div className="d-flex">
                {relationshipToHeader(relationship)}
                {relatedBillTitleText}
                <FeedbackIndicator
                  place="bottom"
                  billId={billId}
                  icon={<ValueBadge value={`${displayedRoundedScore}%`} />}
                  type="identical"
                  tooltipDescription={getTooltipMessage()}
                  infoLink="https://help.pluralpolicy.com/what-are-highly-similar-bills"
                  className="identical-bills"
                  originalBillVersion={billVersion.id}
                  recommendedBillId={identicalBill.billId}
                  recommendedBillVersion={correctBillVersion(identicalBill)?.id}
                />
              </div>
            ) : (
              <div>
                {relationshipToHeader(relationship)}
                {relatedBillTitleText}
              </div>
            )}
          </div>
          <Link
            key={`related-${billId}`}
            target="_blank"
            rel="noopener noreferrer"
            to={`/legislative-tracking/bill/details/${billId}/${billVersionUrl}`}
            className="related-link"
          >
            {billNumber}
            {identicalBill ? (
              <span> ({correctBillVersion(identicalBill)?.versionName})</span>
            ) : (
              <></>
            )}
          </Link>
          {': '}
          {billName}
        </div>
      </div>
    </li>
  );
};

export default RelatedBillItem;
